import { Controller } from "@hotwired/stimulus"
import { presetRange, setPickerDate } from "../helpers/date_presets"

export default class extends Controller {
  static targets = [
    "loadingOverlay",
    "exportContent",
    "errorMessage",
    "errorText",
    "successMessage",
    "reportTypeFilter",
    "dateFrom",
    "dateTo",
    "includeChildren",
    "includeDrafts",
    "appendReports",
  ]

  static values = {
    exportUrl: String,
  }

  connect() {
    this.resetDialogState()
  }

  resetDialogState() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden", "opacity-50", "pointer-events-none")
    }
  }

  exportMerged(event) {
    event.preventDefault()
    this.performExport("pdf_merged")
  }

  exportZip(event) {
    event.preventDefault()
    this.performExport("pdf_zip")
  }

  // Sums the selected reports into one document. Signed reports only, unless drafts are
  // included — the server then marks their share as "nicht freigegeben".
  exportSummary(event) {
    event.preventDefault()
    this.performExport("pdf_summary")
  }

  async performExport(exportFormat) {
    this.showLoading()
    this.hideError()
    this.hideSuccess()

    const formData = new FormData()
    formData.append("export_format", exportFormat)

    if (this.hasReportTypeFilterTarget && this.reportTypeFilterTarget.value) {
      formData.append("custom_report_type_id", this.reportTypeFilterTarget.value)
    }
    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      formData.append("report_date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      formData.append("report_date_to", this.dateToTarget.value)
    }
    if (this.hasIncludeChildrenTarget && this.includeChildrenTarget.checked) {
      formData.append("include_child_reports", "1")
    }
    if (exportFormat === "pdf_summary") {
      if (this.hasIncludeDraftsTarget && this.includeDraftsTarget.checked) {
        formData.append("include_drafts", "1")
      }
      if (this.hasAppendReportsTarget && this.appendReportsTarget.checked) {
        formData.append("append_reports", "1")
      }
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: formData,
      })

      if (!response.ok) {
        const errorJson = await response.json().catch(() => null)
        throw new Error(errorJson?.error || `HTTP error! status: ${response.status}`)
      }

      const json = await response.json()

      if (json.success) {
        this.showSuccess()
        return
      }

      if (json.error) {
        console.error("Export failed:", json.error)
        this.showError(json.error)
      }
    } catch (error) {
      console.error("Export failed:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  // Fills both date fields with a calendar week (Monday to Sunday) or month. The hidden
  // ISO inputs are what gets posted.
  applyPreset(event) {
    const [from, to] = presetRange(event.currentTarget.dataset.preset, new Date())
    setPickerDate(this.dateFromTarget, from)
    setPickerDate(this.dateToTarget, to)
  }

  showLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("opacity-50", "pointer-events-none")
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  showError(message) {
    if (this.hasErrorTextTarget && message) {
      this.errorTextTarget.textContent = message
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  showSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("hidden")
    }
  }

  hideSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden")
    }
  }
}
